import Microgame from '../engine/Microgame.js';

export default class PlugGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Branche !`, en: `Plug!`, es: `¡Enchufa!` },
            controls: { fr: `Fais glisser la prise vers la multiprise`, en: `Drag the plug to the socket`, es: `Arrastra el enchufe al tomacorriente` },
            objective: { fr: `Connecte la prise avant la fin de la batterie`, en: `Connect the plug before the battery dies`, es: `Conecta el enchufe antes de que se acabe la batería` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><rect x="3" y="10" width="10" height="8" rx="2" ry="2"/><path d="M13 14H21"/><path d="M7 10V5"/><path d="M11 10V5"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 84, toX: 50, toY: 25, duration: 820 },
                { type: 'wait', duration: 620 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.plug = null;
        this.socket = null;
        this.cord = null;
        this.isDragging = false;
        this.offsetX = 0;
        this.offsetY = 0;
        
        this._isVictoryAnimating = false;
        this._frozenTimerValue = 0;

        // Randomized positions
        this.socketX = this.isPlayback ? 50 : (30 + Math.random() * 40); // 30% to 70%
        this.socketY = this.isPlayback ? 25 : (15 + Math.random() * 20); // 15% to 35%
        this.plugStartX = this.isPlayback ? 50 : (15 + Math.random() * 70);
    }

    setup() {
        this.engine.ui.instruction.textContent = "PLUG IT IN!";

        // Wall Socket (Secondary)
        this.socket = document.createElement('div');
        this.socket.style.position = 'absolute';
        this.socket.style.width = '70px';
        this.socket.style.height = '70px';
        this.socket.style.background = this.secondaryColor;
        this.socket.style.borderRadius = '12px';
        this.socket.style.top = `${this.socketY}%`;
        this.socket.style.left = `${this.socketX}%`;
        this.socket.style.transform = 'translate(-50%, -50%)';
        this.socket.style.boxShadow = `0 0 20px ${this.secondaryColor}`;
        this.container.appendChild(this.socket);

        // Holes (Dark)
        const hole1 = document.createElement('div');
        hole1.style.position = 'absolute';
        hole1.style.width = '12px';
        hole1.style.height = '24px';
        hole1.style.background = '#000';
        hole1.style.top = '23px';
        hole1.style.left = '18px';
        hole1.style.borderRadius = '2px';
        this.socket.appendChild(hole1);

        const hole2 = hole1.cloneNode();
        hole2.style.left = '40px';
        this.socket.appendChild(hole2);

        // Cord (SVG)
        this.cord = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.cord.style.position = 'absolute';
        this.cord.style.width = '100%';
        this.cord.style.height = '100%';
        this.cord.style.pointerEvents = 'none';
        this.cord.style.filter = `drop-shadow(0 0 5px ${this.primaryColor})`;
        this.cord.innerHTML = `<path d="" stroke="${this.primaryColor}" stroke-width="8" fill="none" stroke-linecap="round"/>`;
        this.container.appendChild(this.cord);

        // Plug (Primary)
        this.plug = document.createElement('div');
        this.plug.style.position = 'absolute';
        this.plug.style.width = '55px';
        this.plug.style.height = '45px';
        this.plug.style.background = this.primaryColor;
        this.plug.style.borderRadius = '6px';
        this.plug.style.bottom = '15%';
        this.plug.style.left = `${this.plugStartX}%`;
        this.plug.style.transform = 'translateX(-50%)';
        this.plug.style.cursor = 'grab';
        this.plug.style.touchAction = 'none';
        this.plug.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.plug.style.zIndex = '20';

        // Prongs (Secondary)
        const prong1 = document.createElement('div');
        prong1.style.position = 'absolute';
        prong1.style.width = '10px';
        prong1.style.height = '18px';
        prong1.style.background = this.secondaryColor;
        prong1.style.top = '-18px';
        prong1.style.left = '12px';
        prong1.style.borderRadius = '2px 2px 0 0';
        this.plug.appendChild(prong1);

        const prong2 = prong1.cloneNode();
        prong2.style.left = '33px';
        this.plug.appendChild(prong2);

        this.container.appendChild(this.plug);

        this.updateCord();

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.plug.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (this._isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }
        if (!this.active || this.won) return;
        
        // Minor "physics" for cord sag - only if dragging
        if (this.isDragging) {
            this.updateCord();
        }
    }

    updateCord() {
        const pRect = this.plug.getBoundingClientRect();
        const cRect = this.container.getBoundingClientRect();

        // Start point (Bottom of plug)
        const x1 = pRect.left + pRect.width / 2 - cRect.left;
        const y1 = pRect.top + pRect.height - cRect.top;

        // Anchor point (Bottom of screen)
        const x2 = (this.plugStartX / 100) * cRect.width;
        const y2 = cRect.height;

        // Control point for "slack" - hangs lower when distance is small
        const midX = (x1 + x2) / 2;
        const midY = Math.max(y1, y2) + 100;

        const path = this.cord.querySelector('path');
        path.setAttribute('d', `M${x1},${y1} Q${midX},${midY} ${x2},${y2}`);
    }

    onDown(e) {
        if (!this.active || this.won || this._isVictoryAnimating) return;
        this.isDragging = true;
        this.plug.style.cursor = 'grabbing';
        this.plug.style.transition = 'none';

        const rect = this.plug.getBoundingClientRect();
        this.offsetX = e.clientX - rect.left;
        this.offsetY = e.clientY - rect.top;
    }

    onMove(e) {
        if (!this.isDragging || !this.active || this.won) return;

        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left - this.offsetX;
        const y = e.clientY - rect.top - this.offsetY;

        this.plug.style.left = `${x}px`;
        this.plug.style.top = `${y}px`;
        this.plug.style.bottom = 'auto';
        this.plug.style.transform = 'none';

        this.updateCord();

        // Check Connection
        const sRect = this.socket.getBoundingClientRect();
        const px = x + 55/2;
        const py = y;
        const sx = sRect.left + sRect.width/2 - rect.left;
        const sy = sRect.top + sRect.height/2 - rect.top;

        const dist = Math.hypot(px - sx, py - sy);

        if (dist < 35) {
            this.handleSuccess(sx, sy);
        }
    }

    handleSuccess(sx, sy) {
        this.won = true;
        this.isDragging = false;
        
        this._isVictoryAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        // 1. Hide Prongs (they are inside the wall now)
        const prongs = this.plug.querySelectorAll('div');
        prongs.forEach(p => p.style.opacity = '0');

        // 2. Snap & Visuals
        this.plug.style.transition = 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.plug.style.left = `${sx}px`;
        this.plug.style.top = `${sy + 10}px`; // Sits slightly lower in socket
        this.plug.style.transform = 'translate(-50%, -50%)';
        this.plug.style.boxShadow = `0 0 30px ${this.secondaryColor}`;
        this.socket.style.background = this.primaryColor;
        this.socket.style.boxShadow = `0 0 30px ${this.primaryColor}`;

        // 3. Keep Cord Following during the 200ms transition
        const startTime = performance.now();
        const animateCord = (time) => {
            this.updateCord();
            if (time - startTime < 250) { // Small buffer over 200ms
                requestAnimationFrame(animateCord);
            }
        };
        requestAnimationFrame(animateCord);

        // 4. Exclamation Marks Animation
        for (let i = 0; i < 6; i++) {
            const angle = (i / 6) * Math.PI * 2;
            const ex = document.createElement('div');
            ex.textContent = '!';
            ex.style.position = 'absolute';
            ex.style.left = `${sx}px`;
            ex.style.top = `${sy}px`;
            ex.style.color = this.secondaryColor;
            ex.style.fontSize = '2rem';
            ex.style.fontWeight = 'bold';
            ex.style.pointerEvents = 'none';
            ex.style.zIndex = '30';
            ex.style.transition = 'all 0.4s ease-out';
            ex.style.transform = 'translate(-50%, -50%) scale(0)';
            ex.style.textShadow = `0 0 10px ${this.secondaryColor}`;
            this.container.appendChild(ex);

            requestAnimationFrame(() => {
                const tx = Math.cos(angle) * 80;
                const ty = Math.sin(angle) * 80;
                ex.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(1.5)`;
                ex.style.opacity = '0';
            });
        }

        // 5. Finalize
        setTimeout(() => {
            this._isVictoryAnimating = false;
            this.win();
        }, 800);
    }

    onUp() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.plug.style.cursor = 'grab';
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
